import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardMedia,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  LocalOffer as PriceIcon,
  QrCode as QrCodeIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useVenue } from '../../contexts/VenueContext';
import { getPackageById, getVenueById, deletePackage } from '../../services/venueService';
import { Package, Venue } from '../../types/Venue';
// import QRCode from 'react-qr-code';

const PackageDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { venueId, packageId } = useParams<{ venueId: string; packageId: string }>();
  const navigate = useNavigate();
  
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [venueData, setVenueData] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareSuccess, setShareSuccess] = useState(false);

  // Current timestamp for package availability checking
  const now = new Date();
  const currentDay = now.getDay(); // 0-6 for Sunday-Saturday

  useEffect(() => {
    const fetchData = async () => {
      if (!venueId || !packageId) {
        setError('Invalid venue or package ID');
        setLoading(false);
        return;
      }

      try {
        // Fetch package details
        const packageDetails = await getPackageById(venueId, packageId);
        setPackageData(packageDetails);
        
        // Fetch venue details
        const venueDetails = await getVenueById(venueId);
        setVenueData(venueDetails);
        
        // Generate share URL
        const baseUrl = window.location.origin;
        setShareUrl(`${baseUrl}/explore/packages/${venueId}/${packageId}`);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load package details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [venueId, packageId]);

  const handleEditPackage = () => {
    navigate(`/venues/${venueId}/packages/${packageId}/edit`);
  };

  const handleDeleteConfirmOpen = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
  };

  const handleDeletePackage = async () => {
    if (!venueId || !packageId) return;
    
    try {
      await deletePackage(venueId, packageId);
      navigate(`/venues/${venueId}`);
    } catch (err) {
      console.error('Error deleting package:', err);
      setError('Failed to delete package. Please try again.');
    }
    
    setDeleteConfirmOpen(false);
  };

  const handleShareClick = () => {
    // Open the QR dialog which now includes social media sharing options
    setQrDialogOpen(true);
  };

  // Social media sharing handlers
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
    trackShare('Facebook');
  };

  const shareToTwitter = () => {
    const text = `Check out this amazing date package: ${packageData?.name} at ${venueData?.name}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
    trackShare('Twitter');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
    trackShare('LinkedIn');
  };

  const shareToWhatsApp = () => {
    const text = `Check out this date package: ${packageData?.name} at ${venueData?.name}: ${shareUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    trackShare('WhatsApp');
  };

  const shareByEmail = () => {
    const subject = `Check out this date package: ${packageData?.name}`;
    const body = `I found this amazing date package at ${venueData?.name} and thought you might be interested!\n\n${packageData?.description}\n\nCheck it out here: ${shareUrl}`;
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url);
    trackShare('Email');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      })
      .catch(err => console.error('Could not copy text: ', err));
    trackShare('Copy');
  };

  // Track sharing metrics
  const trackShare = (platform: string) => {
    // This function could be expanded to track sharing analytics
    console.log(`Package shared on ${platform} at ${new Date().toISOString()}`);
    // Here you would typically call an analytics tracking service
  };

  const handleQrDialogOpen = () => {
    setQrDialogOpen(true);
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
  };

  const isPackageAvailableToday = () => {
    if (!packageData?.availability?.daysAvailable) return false;
    return packageData.availability.daysAvailable.includes(currentDay);
  };

  // Format time from 24h to 12h format
  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return time;
    }
  };

  const renderTimeSlots = () => {
    if (!packageData?.availability?.timeSlots || packageData.availability.timeSlots.length === 0) {
      return <Typography variant="body2">No specific time slots available</Typography>;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {packageData.availability.timeSlots.map((slot, index) => (
          <Chip
            key={index}
            label={`${formatTime(slot.start)} - ${formatTime(slot.end)}`}
            variant="outlined"
            color={isPackageAvailableToday() ? "primary" : "default"}
            size="small"
          />
        ))}
      </Box>
    );
  };

  // Renders the availability days as chips
  const renderAvailabilityDays = () => {
    if (!packageData?.availability?.daysAvailable || packageData.availability.daysAvailable.length === 0) {
      return <Typography variant="body2">Not currently available</Typography>;
    }

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {days.map((day, index) => (
          <Chip
            key={index}
            label={day}
            variant="outlined"
            color={packageData.availability.daysAvailable.includes(index) ? "primary" : "default"}
            size="small"
            sx={{
              opacity: packageData.availability.daysAvailable.includes(index) ? 1 : 0.5
            }}
          />
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !packageData || !venueData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Package or venue not found'}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(`/venues/${venueId}`)}
          sx={{ mt: 2 }}
        >
          Back to Venue
        </Button>
      </Container>
    );
  }

  return (
     <Container maxWidth='lg' sx={{ py: 4 }}>
        {/* Header with back button and actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
           <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/venues/${venueId}`)}>
              Back to {venueData.name}
           </Button>

           <Box>
              <Button
                 variant='contained'
                 color='primary'
                 startIcon={<ShareIcon />}
                 onClick={handleShareClick}
                 sx={{ mr: 2 }}
              >
                 Share Package
              </Button>

              <Tooltip title='Edit Package'>
                 <IconButton onClick={handleEditPackage} sx={{ mr: 1 }}>
                    <EditIcon />
                 </IconButton>
              </Tooltip>

              <Tooltip title='Delete Package'>
                 <IconButton onClick={handleDeleteConfirmOpen} color='error'>
                    <DeleteIcon />
                 </IconButton>
              </Tooltip>
           </Box>
        </Box>

        {/* Package Hero Section */}
        <Paper elevation={3} sx={{ overflow: 'hidden', mb: 4 }}>
           <Grid container>
              {/* Package Image */}
              <Grid item xs={12} md={6}>
                 <Box sx={{ position: 'relative', height: '100%', minHeight: 300 }}>
                    <CardMedia
                       component='img'
                       image={
                          packageData.images[0] ||
                          'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
                       }
                       alt={packageData.name}
                       sx={{ height: '100%', minHeight: 300, objectFit: 'cover' }}
                    />

                    {/* Price Tag */}
                    <Box
                       sx={{
                          position: 'absolute',
                          bottom: 20,
                          right: 20,
                          bgcolor: 'primary.main',
                          color: 'white',
                          py: 1,
                          px: 2,
                          borderRadius: 1,
                          fontWeight: 'bold',
                          boxShadow: 2,
                       }}
                    >
                       ${packageData.price.toFixed(2)}
                       {packageData.discountedPrice && (
                          <Typography
                             component='span'
                             sx={{
                                ml: 1,
                                textDecoration: 'line-through',
                                opacity: 0.7,
                                fontSize: '0.8em',
                             }}
                          >
                             ${packageData.discountedPrice.toFixed(2)}
                          </Typography>
                       )}
                    </Box>

                    {/* Active Status */}
                    {packageData.isActive ? (
                       <Chip
                          icon={<CheckIcon />}
                          label='Active'
                          color='success'
                          sx={{
                             position: 'absolute',
                             top: 20,
                             right: 20,
                          }}
                       />
                    ) : (
                       <Chip
                          label='Inactive'
                          color='default'
                          sx={{
                             position: 'absolute',
                             top: 20,
                             right: 20,
                          }}
                       />
                    )}
                 </Box>
              </Grid>

              {/* Package Details */}
              <Grid item xs={12} md={6}>
                 <Box sx={{ p: 3 }}>
                    {/* Package Type */}
                    <Chip
                       label={
                          packageData.packageType.charAt(0).toUpperCase() +
                          packageData.packageType.slice(1)
                       }
                       size='small'
                       color='primary'
                       variant='outlined'
                       sx={{ mb: 1 }}
                    />

                    {/* Package Name */}
                    <Typography variant='h4' component='h1' gutterBottom>
                       {packageData.name}
                    </Typography>

                    {/* Package Description */}
                    <Typography variant='body1' paragraph>
                       {packageData.description}
                    </Typography>

                    {/* Location */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                       <PlaceIcon sx={{ color: 'text.secondary', mr: 1 }} />
                       <Typography variant='body2'>
                          {venueData.location.address}, {venueData.location.city}
                       </Typography>
                    </Box>

                    {/* Package Details Grid */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                       {/* Capacity */}
                       <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                             <GroupIcon sx={{ color: 'text.secondary', mr: 1 }} />
                             <Typography variant='body2'>
                                For up to {packageData.maxCapacity}{' '}
                                {packageData.maxCapacity > 1 ? 'people' : 'person'}
                             </Typography>
                          </Box>
                       </Grid>

                       {/* Price */}
                       <Grid item xs={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                             <PriceIcon sx={{ color: 'text.secondary', mr: 1 }} />
                             <Typography variant='body2'>
                                ${packageData.price.toFixed(2)} per package
                             </Typography>
                          </Box>
                       </Grid>

                       {/* Available Days */}
                       <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                             <CalendarIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.5 }} />
                             <Box>
                                <Typography variant='body2' gutterBottom>
                                   Available Days:
                                </Typography>
                                {renderAvailabilityDays()}
                             </Box>
                          </Box>
                       </Grid>

                       {/* Time Slots */}
                       <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                             <ScheduleIcon sx={{ color: 'text.secondary', mr: 1, mt: 0.5 }} />
                             <Box>
                                <Typography variant='body2' gutterBottom>
                                   Time Slots:
                                </Typography>
                                {renderTimeSlots()}
                             </Box>
                          </Box>
                       </Grid>
                    </Grid>

                    {/* CTA Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                       <Button
                          variant='contained'
                          color='primary'
                          fullWidth
                          disabled={!packageData.isActive}
                          sx={{ py: 1 }}
                       >
                          Purchase Package
                       </Button>

                       <Button
                          variant='outlined'
                          startIcon={<QrCodeIcon />}
                          onClick={handleQrDialogOpen}
                       >
                          QR Code
                       </Button>
                    </Box>
                 </Box>
              </Grid>
           </Grid>
        </Paper>

        {/* Included Items Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
           <Typography variant='h6' gutterBottom>
              What's Included
           </Typography>
           <List>
              {packageData.includesItems.map((item, index) => (
                 <React.Fragment key={index}>
                    {index > 0 && <Divider component='li' />}
                    <ListItem>
                       <ListItemIcon>
                          <CheckIcon color='success' />
                       </ListItemIcon>
                       <ListItemText primary={item} />
                    </ListItem>
                 </React.Fragment>
              ))}
           </List>
        </Paper>

        {/* Package Images Gallery */}
        {packageData.images.length > 1 && (
           <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant='h6' gutterBottom>
                 Gallery
              </Typography>
              <Grid container spacing={2}>
                 {packageData.images.slice(1).map((image, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                       <Card sx={{ height: '100%' }}>
                          <CardMedia
                             component='img'
                             height={140}
                             image={image}
                             alt={`Gallery image ${index + 1}`}
                             sx={{ objectFit: 'cover' }}
                          />
                       </Card>
                    </Grid>
                 ))}
              </Grid>
           </Paper>
        )}

        {/* Restrictions Section (if any) */}
        {packageData.restrictions && packageData.restrictions.length > 0 && (
           <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant='h6' gutterBottom>
                 Restrictions & Requirements
              </Typography>
              <List dense>
                 {packageData.restrictions.map((restriction, index) => (
                    <ListItem key={index}>
                       <ListItemText primary={restriction} />
                    </ListItem>
                 ))}
              </List>
           </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose}>
           <DialogTitle>Confirm Delete</DialogTitle>
           <DialogContent>
              <Typography>
                 Are you sure you want to delete this package? This action cannot be undone.
              </Typography>
           </DialogContent>
           <DialogActions>
              <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
              <Button onClick={handleDeletePackage} color='error'>
                 Delete
              </Button>
           </DialogActions>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onClose={handleQrDialogClose} maxWidth='md' fullWidth>
           <DialogTitle>
              Share Package on Social Media
              <Typography variant='subtitle2' color='text.secondary'>
                 Scan the QR code or share directly to your favorite platform
              </Typography>
           </DialogTitle>
           <DialogContent>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                 <Grid container spacing={3} alignItems='center'>
                    <Grid
                       item
                       xs={12}
                       md={5}
                       sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                       <Typography variant='h6' gutterBottom>
                          QR Code
                       </Typography>
                       <Paper elevation={2} sx={{ p: 3, mb: 2, display: 'inline-block' }}>
                          {/* <QRCode 
                    value={`https://wetrippen.com/venues/${venueId}/packages/${packageId}`}
                    size={180}
                  /> */}
                       </Paper>
                       <Typography variant='body2'>
                          Scan this code to view or purchase this package
                       </Typography>
                    </Grid>

                    <Grid item xs={12} md={7}>
                       <Typography variant='h6' gutterBottom>
                          Share on Social Media
                       </Typography>

                       <TextField
                          value={shareUrl}
                          fullWidth
                          variant='outlined'
                          size='small'
                          sx={{ mb: 3 }}
                          InputProps={{
                             readOnly: true,
                             endAdornment: (
                                <Button
                                   startIcon={<CopyIcon />}
                                   onClick={copyToClipboard}
                                   variant='contained'
                                   size='small'
                                >
                                   Copy
                                </Button>
                             ),
                          }}
                       />

                       {shareSuccess && (
                          <Alert severity='success' sx={{ mb: 2 }}>
                             Link copied to clipboard!
                          </Alert>
                       )}

                       <Grid container spacing={2}>
                          <Grid item xs={6} md={4}>
                             <Button
                                startIcon={<FacebookIcon />}
                                onClick={shareToFacebook}
                                fullWidth
                                variant='outlined'
                                color='primary'
                                sx={{ mb: 2 }}
                             >
                                Facebook
                             </Button>
                          </Grid>
                          <Grid item xs={6} md={4}>
                             <Button
                                startIcon={<TwitterIcon />}
                                onClick={shareToTwitter}
                                fullWidth
                                variant='outlined'
                                color='primary'
                                sx={{ mb: 2 }}
                             >
                                Twitter
                             </Button>
                          </Grid>
                          <Grid item xs={6} md={4}>
                             <Button
                                startIcon={<LinkedInIcon />}
                                onClick={shareToLinkedIn}
                                fullWidth
                                variant='outlined'
                                color='primary'
                                sx={{ mb: 2 }}
                             >
                                LinkedIn
                             </Button>
                          </Grid>
                          <Grid item xs={6} md={4}>
                             <Button
                                startIcon={<WhatsAppIcon />}
                                onClick={shareToWhatsApp}
                                fullWidth
                                variant='outlined'
                                color='success'
                                sx={{ mb: 2 }}
                             >
                                WhatsApp
                             </Button>
                          </Grid>
                          <Grid item xs={6} md={4}>
                             <Button
                                startIcon={<EmailIcon />}
                                onClick={shareByEmail}
                                fullWidth
                                variant='outlined'
                                color='secondary'
                             >
                                Email
                             </Button>
                          </Grid>
                          <Grid item xs={6} md={4}>
                             <Button
                                startIcon={<QrCodeIcon />}
                                onClick={() => window.print()}
                                fullWidth
                                variant='outlined'
                             >
                                Print QR
                             </Button>
                          </Grid>
                       </Grid>
                    </Grid>
                 </Grid>
              </Box>
           </DialogContent>
           <DialogActions>
              <Button onClick={handleQrDialogClose}>Close</Button>
           </DialogActions>
        </Dialog>
     </Container>
  );
};

export default PackageDetail;
